import React, { useState, useEffect } from 'react';
import { apiService, Event, TicketCategory, MediaItem, Geolocation, CheckoutConfig, AgeRestrictions } from '../services/api';
import FileUpload from './FileUpload';
import MediaCarousel from './MediaCarousel';
import EventAnalytics from './EventAnalytics';
import './EventManager.css';

const EventManager: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
    location: '',
    enabled: true,
    medias: [] as MediaItem[],
    geolocation: null as Geolocation | null,
    ticketCategories: [] as TicketCategory[]
  });

  // Uploaded files tracking for FileUpload component
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  // Media form state
  const [mediaForm, setMediaForm] = useState({
    type: 'image' as 'image' | 'video',
    url: '',
    description: ''
  });

  // Ticket category form state
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    maxQuantity: '',
    maxAttendees: '',
    enabled: true,
    isFree: false,
    validFrom: '',
    validUntil: '',
    ageRestrictions: {
      minAge: '',
      maxAge: '',
      requiresGuardian: false,
      ageVerificationRequired: false
    },
    checkout: {
      provider: 'stripe',
      publicKey: '',
      successUrl: '',
      cancelUrl: ''
    }
  });

  // Geolocation form state
  const [geoForm, setGeoForm] = useState({
    latitude: '',
    longitude: '',
    address: ''
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEvents();
      setEvents(data);
    } catch (err: any) {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const eventData = {
        ...formData,
        medias: formData.medias.length > 0 ? formData.medias : undefined,
        geolocation: formData.geolocation || undefined,
        ticketCategories: formData.ticketCategories.length > 0 ? formData.ticketCategories : [
          {
            id: crypto.randomUUID(),
            name: 'General Admission',
            description: 'Standard event ticket',
            price: 0,
            currency: 'USD',
            enabled: true,
            isFree: true,
            currentSold: 0
          }
        ]
      };

      if (editingEvent) {
        await apiService.updateEvent(editingEvent.id, eventData);
      } else {
        await apiService.createEvent(eventData);
      }

      setShowCreateForm(false);
      setEditingEvent(null);
      resetForm();
      loadEvents();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      startDateTime: event.startDateTime,
      endDateTime: event.endDateTime,
      location: event.location,
      enabled: event.enabled,
      medias: event.medias || [],
      geolocation: event.geolocation || null,
      ticketCategories: event.ticketCategories || []
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await apiService.deleteEvent(id);
        loadEvents();
      } catch (err: any) {
        setError('Failed to delete event');
      }
    }
  };

  const handleViewDetails = (event: Event) => {
    setSelectedEvent(event);
    setShowEventDetail(true);
  };

  const handleViewAnalytics = (event: Event) => {
    setSelectedEvent(event);
    setShowAnalytics(true);
  };

  const closeModals = () => {
    setShowEventDetail(false);
    setShowAnalytics(false);
    setSelectedEvent(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDateTime: '',
      endDateTime: '',
      location: '',
      enabled: true,
      medias: [],
      geolocation: null,
      ticketCategories: []
    });
    setCategoryForm({
      name: '',
      description: '',
      price: '',
      currency: 'USD',
      maxQuantity: '',
      maxAttendees: '',
      enabled: true,
      isFree: false,
      validFrom: '',
      validUntil: '',
      ageRestrictions: {
        minAge: '',
        maxAge: '',
        requiresGuardian: false,
        ageVerificationRequired: false
      },
      checkout: {
        provider: 'stripe',
        publicKey: '',
        successUrl: '',
        cancelUrl: ''
      }
    });
    setMediaForm({
      type: 'image',
      url: '',
      description: ''
    });
    setGeoForm({
      latitude: '',
      longitude: '',
      address: ''
    });
  };

  const addMedia = () => {
    if (mediaForm.url) {
      setFormData(prev => ({
        ...prev,
        medias: [...prev.medias, { ...mediaForm }]
      }));
      setMediaForm({ type: 'image', url: '', description: '' });
    }
  };

  const removeMedia = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medias: prev.medias.filter((_, i) => i !== index)
    }));
  };

  const addGeolocation = () => {
    if (geoForm.latitude && geoForm.longitude) {
      setFormData(prev => ({
        ...prev,
        geolocation: {
          latitude: parseFloat(geoForm.latitude),
          longitude: parseFloat(geoForm.longitude),
          address: geoForm.address || undefined
        }
      }));
      setGeoForm({ latitude: '', longitude: '', address: '' });
    }
  };

  const removeGeolocation = () => {
    setFormData(prev => ({ ...prev, geolocation: null }));
  };

  const addTicketCategory = () => {
    if (categoryForm.name && categoryForm.price !== '') {
      const ageRestrictions: AgeRestrictions = {};
      if (categoryForm.ageRestrictions.minAge) ageRestrictions.minAge = parseInt(categoryForm.ageRestrictions.minAge);
      if (categoryForm.ageRestrictions.maxAge) ageRestrictions.maxAge = parseInt(categoryForm.ageRestrictions.maxAge);
      if (categoryForm.ageRestrictions.requiresGuardian) ageRestrictions.requiresGuardian = true;
      if (categoryForm.ageRestrictions.ageVerificationRequired) ageRestrictions.ageVerificationRequired = true;

      const checkout: CheckoutConfig | undefined = !categoryForm.isFree && parseFloat(categoryForm.price) > 0 ? {
        provider: categoryForm.checkout.provider,
        publicKey: categoryForm.checkout.publicKey || undefined,
        successUrl: categoryForm.checkout.successUrl || undefined,
        cancelUrl: categoryForm.checkout.cancelUrl || undefined
      } : undefined;

      const newCategory: TicketCategory = {
        id: crypto.randomUUID(),
        name: categoryForm.name,
        description: categoryForm.description || undefined,
        price: parseFloat(categoryForm.price),
        currency: categoryForm.currency,
        maxQuantity: categoryForm.maxQuantity ? parseInt(categoryForm.maxQuantity) : undefined,
        maxAttendees: categoryForm.maxAttendees ? parseInt(categoryForm.maxAttendees) : undefined,
        currentSold: 0,
        enabled: categoryForm.enabled,
        isFree: categoryForm.isFree || parseFloat(categoryForm.price) === 0,
        validFrom: categoryForm.validFrom || undefined,
        validUntil: categoryForm.validUntil || undefined,
        ageRestrictions: Object.keys(ageRestrictions).length > 0 ? ageRestrictions : undefined,
        checkout
      };

      setFormData(prev => ({
        ...prev,
        ticketCategories: [...prev.ticketCategories, newCategory]
      }));

      setCategoryForm({
        name: '',
        description: '',
        price: '',
        currency: 'USD',
        maxQuantity: '',
        maxAttendees: '',
        enabled: true,
        isFree: false,
        validFrom: '',
        validUntil: '',
        ageRestrictions: {
          minAge: '',
          maxAge: '',
          requiresGuardian: false,
          ageVerificationRequired: false
        },
        checkout: {
          provider: 'stripe',
          publicKey: '',
          successUrl: '',
          cancelUrl: ''
        }
      });
    }
  };

  const removeTicketCategory = (index: number) => {
    setFormData(prev => ({
      ...prev,
      ticketCategories: prev.ticketCategories.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="event-manager">
      <div className="manager-header">
        <h2>Event Management</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            setShowCreateForm(true);
            setEditingEvent(null);
            resetForm();
          }}
        >
          Create New Event
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {showCreateForm && (
        <div className="create-form-overlay">
          <div className="create-form-modal">
            <div className="form-header">
              <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingEvent(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="event-form">
              {/* Basic Event Information */}
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-group">
                  <label htmlFor="title">Event Title *</label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="startDateTime">Start Date & Time *</label>
                    <input
                      type="datetime-local"
                      id="startDateTime"
                      value={formData.startDateTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDateTime: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="endDateTime">End Date & Time *</label>
                    <input
                      type="datetime-local"
                      id="endDateTime"
                      value={formData.endDateTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDateTime: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <input
                    type="text"
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    Event Enabled
                  </label>
                </div>
              </div>

              {/* Media Section */}
              <div className="form-section">
                <h4>Media</h4>
                
                {/* File Upload Section */}
                <div className="upload-section">
                  <FileUpload
                    onFileUploaded={(file) => {
                      const mediaType = file.type === 'image' ? 'image' : 'video';
                      const newMedia: MediaItem = {
                        type: mediaType,
                        url: file.url,
                        description: file.description || file.name
                      };
                      setFormData(prev => ({
                        ...prev,
                        medias: [...prev.medias, newMedia]
                      }));
                      setUploadedFiles(prev => [...prev, file]);
                    }}
                    onFileRemoved={(fileId) => {
                      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
                      // Also remove from medias if needed
                      setFormData(prev => ({
                        ...prev,
                        medias: prev.medias.filter(m => !m.url.includes(fileId))
                      }));
                    }}
                    uploadedFiles={uploadedFiles}
                    accept={{
                      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
                      'video/*': ['.mp4', '.webm', '.ogg', '.avi', '.mov']
                    }}
                    maxSize={50 * 1024 * 1024} // 50MB
                  />
                </div>

                {/* Manual URL Entry (Alternative) */}
                <div className="media-form">
                  <h5>Or add media by URL:</h5>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="mediaType">Type</label>
                      <select
                        id="mediaType"
                        value={mediaForm.type}
                        onChange={(e) => setMediaForm(prev => ({ ...prev, type: e.target.value as 'image' | 'video' }))}
                      >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mediaUrl">URL</label>
                      <input
                        type="url"
                        id="mediaUrl"
                        value={mediaForm.url}
                        onChange={(e) => setMediaForm(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="mediaDescription">Description</label>
                      <input
                        type="text"
                        id="mediaDescription"
                        value={mediaForm.description}
                        onChange={(e) => setMediaForm(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>

                    <button type="button" onClick={addMedia} className="btn btn-secondary">
                      Add Media
                    </button>
                  </div>
                </div>

                {formData.medias.length > 0 && (
                  <div className="media-list">
                    <h5>Added Media:</h5>
                    {formData.medias.map((media, index) => (
                      <div key={index} className="media-item">
                        <span className="media-type">{media.type}</span>
                        <span className="media-url">{media.url}</span>
                        <span className="media-description">{media.description}</span>
                        <button type="button" onClick={() => removeMedia(index)} className="btn btn-danger btn-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Geolocation Section */}
              <div className="form-section">
                <h4>Geolocation</h4>
                
                {!formData.geolocation ? (
                  <div className="geolocation-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="latitude">Latitude</label>
                        <input
                          type="number"
                          step="any"
                          id="latitude"
                          value={geoForm.latitude}
                          onChange={(e) => setGeoForm(prev => ({ ...prev, latitude: e.target.value }))}
                          placeholder="51.5074"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="longitude">Longitude</label>
                        <input
                          type="number"
                          step="any"
                          id="longitude"
                          value={geoForm.longitude}
                          onChange={(e) => setGeoForm(prev => ({ ...prev, longitude: e.target.value }))}
                          placeholder="-0.1278"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="geoAddress">Address (optional)</label>
                        <input
                          type="text"
                          id="geoAddress"
                          value={geoForm.address}
                          onChange={(e) => setGeoForm(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="London, UK"
                        />
                      </div>

                      <button type="button" onClick={addGeolocation} className="btn btn-secondary">
                        Add Location
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="geolocation-display">
                    <p>
                      📍 {formData.geolocation.latitude}, {formData.geolocation.longitude}
                      {formData.geolocation.address && ` (${formData.geolocation.address})`}
                    </p>
                    <button type="button" onClick={removeGeolocation} className="btn btn-danger btn-sm">
                      Remove Location
                    </button>
                  </div>
                )}
              </div>

              {/* Ticket Categories Section */}
              <div className="form-section">
                <h4>Ticket Categories</h4>
                
                <div className="category-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="categoryName">Category Name</label>
                      <input
                        type="text"
                        id="categoryName"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="General Admission"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="categoryPrice">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        id="categoryPrice"
                        value={categoryForm.price}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="categoryCurrency">Currency</label>
                      <select
                        id="categoryCurrency"
                        value={categoryForm.currency}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, currency: e.target.value }))}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="categoryDescription">Description</label>
                    <input
                      type="text"
                      id="categoryDescription"
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Standard event ticket"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="maxQuantity">Max per Purchase</label>
                      <input
                        type="number"
                        id="maxQuantity"
                        value={categoryForm.maxQuantity}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, maxQuantity: e.target.value }))}
                        placeholder="10"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="maxAttendees">Total Available</label>
                      <input
                        type="number"
                        id="maxAttendees"
                        value={categoryForm.maxAttendees}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, maxAttendees: e.target.value }))}
                        placeholder="100"
                      />
                    </div>
                  </div>

                  {/* Age Restrictions */}
                  <div className="age-restrictions">
                    <h5>Age Restrictions</h5>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="minAge">Minimum Age</label>
                        <input
                          type="number"
                          id="minAge"
                          value={categoryForm.ageRestrictions.minAge}
                          onChange={(e) => setCategoryForm(prev => ({
                            ...prev,
                            ageRestrictions: { ...prev.ageRestrictions, minAge: e.target.value }
                          }))}
                          placeholder="18"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="maxAge">Maximum Age</label>
                        <input
                          type="number"
                          id="maxAge"
                          value={categoryForm.ageRestrictions.maxAge}
                          onChange={(e) => setCategoryForm(prev => ({
                            ...prev,
                            ageRestrictions: { ...prev.ageRestrictions, maxAge: e.target.value }
                          }))}
                          placeholder="65"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={categoryForm.ageRestrictions.requiresGuardian}
                          onChange={(e) => setCategoryForm(prev => ({
                            ...prev,
                            ageRestrictions: { ...prev.ageRestrictions, requiresGuardian: e.target.checked }
                          }))}
                        />
                        Requires Guardian Consent for Minors
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={categoryForm.ageRestrictions.ageVerificationRequired}
                          onChange={(e) => setCategoryForm(prev => ({
                            ...prev,
                            ageRestrictions: { ...prev.ageRestrictions, ageVerificationRequired: e.target.checked }
                          }))}
                        />
                        Age Verification Required at Entrance
                      </label>
                    </div>
                  </div>

                  {/* Checkout Configuration for paid tickets */}
                  {!categoryForm.isFree && parseFloat(categoryForm.price || '0') > 0 && (
                    <div className="checkout-config">
                      <h5>Payment Configuration</h5>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="provider">Payment Provider</label>
                          <select
                            id="provider"
                            value={categoryForm.checkout.provider}
                            onChange={(e) => setCategoryForm(prev => ({
                              ...prev,
                              checkout: { ...prev.checkout, provider: e.target.value }
                            }))}
                          >
                            <option value="stripe">Stripe</option>
                            <option value="paypal">PayPal</option>
                            <option value="mercadopago">MercadoPago</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="publicKey">Public Key</label>
                          <input
                            type="text"
                            id="publicKey"
                            value={categoryForm.checkout.publicKey}
                            onChange={(e) => setCategoryForm(prev => ({
                              ...prev,
                              checkout: { ...prev.checkout, publicKey: e.target.value }
                            }))}
                            placeholder="pk_test_..."
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="successUrl">Success URL</label>
                          <input
                            type="url"
                            id="successUrl"
                            value={categoryForm.checkout.successUrl}
                            onChange={(e) => setCategoryForm(prev => ({
                              ...prev,
                              checkout: { ...prev.checkout, successUrl: e.target.value }
                            }))}
                            placeholder="https://example.com/success"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="cancelUrl">Cancel URL</label>
                          <input
                            type="url"
                            id="cancelUrl"
                            value={categoryForm.checkout.cancelUrl}
                            onChange={(e) => setCategoryForm(prev => ({
                              ...prev,
                              checkout: { ...prev.checkout, cancelUrl: e.target.value }
                            }))}
                            placeholder="https://example.com/cancel"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={categoryForm.isFree}
                          onChange={(e) => setCategoryForm(prev => ({ ...prev, isFree: e.target.checked }))}
                        />
                        Free Ticket
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={categoryForm.enabled}
                          onChange={(e) => setCategoryForm(prev => ({ ...prev, enabled: e.target.checked }))}
                        />
                        Category Enabled
                      </label>
                    </div>
                  </div>

                  <button type="button" onClick={addTicketCategory} className="btn btn-secondary">
                    Add Category
                  </button>
                </div>

                {formData.ticketCategories.length > 0 && (
                  <div className="categories-list">
                    <h5>Added Categories:</h5>
                    {formData.ticketCategories.map((category, index) => (
                      <div key={index} className="category-item">
                        <div className="category-info">
                          <strong>{category.name}</strong> - {category.currency} {category.price}
                          {category.isFree && <span className="free-badge">FREE</span>}
                          {category.ageRestrictions && (
                            <span className="age-badge">
                              Ages: {category.ageRestrictions.minAge || 0}-{category.ageRestrictions.maxAge || '∞'}
                            </span>
                          )}
                        </div>
                        <button type="button" onClick={() => removeTicketCategory(index)} className="btn btn-danger btn-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingEvent(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="events-list">
        {loading && !showCreateForm && <div className="loading">Loading events...</div>}
        
        {events.length === 0 && !loading && !showCreateForm && (
          <div className="no-events">
            <p>No events found. Create your first event!</p>
          </div>
        )}

        {events.map(event => (
          <div key={event.id} className="event-card">
            <div className="event-card-content">
              {/* Media Preview */}
              {(event.medias && event.medias.length > 0) && (
                <div className="event-media-preview">
                  <MediaCarousel 
                    media={event.medias.map((media, index) => ({
                      id: `${event.id}-media-${index}`,
                      ...media
                    }))} 
                    className="preview-carousel"
                  />
                </div>
              )}
              
              <div className="event-info">
                <h3>{event.title}</h3>
                <p className="event-description">{event.description}</p>
                <div className="event-details">
                  <div className="detail-item">
                    <strong>📅 Date:</strong> {new Date(event.startDateTime).toLocaleString()} - {new Date(event.endDateTime).toLocaleString()}
                  </div>
                  <div className="detail-item">
                    <strong>📍 Location:</strong> {event.location}
                  </div>
                  <div className="detail-item">
                    <strong>🎫 Categories:</strong> {event.ticketCategories?.length || 0}
                  </div>
                  <div className="detail-item">
                    <strong>📸 Media:</strong> {event.medias?.length || 0}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> 
                    <span className={`status ${event.enabled ? 'enabled' : 'disabled'}`}>
                      {event.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="event-actions">
              <button onClick={() => handleViewDetails(event)} className="btn btn-info btn-sm">
                📊 Details
              </button>
              <button onClick={() => handleViewAnalytics(event)} className="btn btn-success btn-sm">
                📈 Analytics
              </button>
              <button onClick={() => handleEdit(event)} className="btn btn-secondary btn-sm">
                ✏️ Edit
              </button>
              <button onClick={() => handleDelete(event.id)} className="btn btn-danger btn-sm">
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Event Details Modal */}
      {showEventDetail && selectedEvent && (
        <div className="modal-overlay">
          <div className="modal-content event-detail-modal">
            <div className="modal-header">
              <h2>📊 Event Details: {selectedEvent.title}</h2>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-sections">
                {/* Event Information */}
                <div className="detail-section">
                  <h3>📋 Event Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Title:</strong> {selectedEvent.title}
                    </div>
                    <div className="detail-item">
                      <strong>Description:</strong> {selectedEvent.description}
                    </div>
                    <div className="detail-item">
                      <strong>Start Date:</strong> {new Date(selectedEvent.startDateTime).toLocaleString()}
                    </div>
                    <div className="detail-item">
                      <strong>End Date:</strong> {new Date(selectedEvent.endDateTime).toLocaleString()}
                    </div>
                    <div className="detail-item">
                      <strong>Location:</strong> {selectedEvent.location}
                    </div>
                    <div className="detail-item">
                      <strong>Status:</strong> 
                      <span className={`status ${selectedEvent.enabled ? 'enabled' : 'disabled'}`}>
                        {selectedEvent.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Media Section */}
                {selectedEvent.medias && selectedEvent.medias.length > 0 && (
                  <div className="detail-section">
                    <h3>📸 Media ({selectedEvent.medias.length})</h3>
                    <MediaCarousel 
                      media={selectedEvent.medias.map((media, index) => ({
                        id: `detail-${selectedEvent.id}-media-${index}`,
                        ...media
                      }))} 
                      className="detail-carousel"
                    />
                  </div>
                )}

                {/* Geolocation Section */}
                {selectedEvent.geolocation && (
                  <div className="detail-section">
                    <h3>📍 Geolocation</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <strong>Latitude:</strong> {selectedEvent.geolocation.latitude}
                      </div>
                      <div className="detail-item">
                        <strong>Longitude:</strong> {selectedEvent.geolocation.longitude}
                      </div>
                      {selectedEvent.geolocation.address && (
                        <div className="detail-item">
                          <strong>Address:</strong> {selectedEvent.geolocation.address}
                        </div>
                      )}
                      <div className="detail-item">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => window.open(`https://www.google.com/maps/@${selectedEvent.geolocation!.latitude},${selectedEvent.geolocation!.longitude},15z`, '_blank')}
                        >
                          🗺️ View on Google Maps
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ticket Categories Section */}
                {selectedEvent.ticketCategories && selectedEvent.ticketCategories.length > 0 && (
                  <div className="detail-section">
                    <h3>🎫 Ticket Categories ({selectedEvent.ticketCategories.length})</h3>
                    <div className="categories-grid">
                      {selectedEvent.ticketCategories.map((category, index) => (
                        <div key={index} className="category-detail-card">
                          <h4>{category.name}</h4>
                          {category.description && <p>{category.description}</p>}
                          <div className="category-info">
                            <div className="info-row">
                              <strong>Price:</strong> 
                              {category.isFree ? 'Free' : `${category.currency} ${category.price.toFixed(2)}`}
                            </div>
                            {category.maxQuantity && (
                              <div className="info-row">
                                <strong>Max per Purchase:</strong> {category.maxQuantity}
                              </div>
                            )}
                            {category.maxAttendees && (
                              <div className="info-row">
                                <strong>Total Available:</strong> {category.maxAttendees}
                              </div>
                            )}
                            <div className="info-row">
                              <strong>Currently Sold:</strong> {category.currentSold || 0}
                            </div>
                            <div className="info-row">
                              <strong>Status:</strong> 
                              <span className={`status ${category.enabled ? 'enabled' : 'disabled'}`}>
                                {category.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            {category.ageRestrictions && (
                              <div className="info-row">
                                <strong>Age Restrictions:</strong>
                                <span>
                                  {category.ageRestrictions.minAge && `Min: ${category.ageRestrictions.minAge}`}
                                  {category.ageRestrictions.maxAge && ` Max: ${category.ageRestrictions.maxAge}`}
                                  {category.ageRestrictions.requiresGuardian && ' (Guardian Required)'}
                                  {category.ageRestrictions.ageVerificationRequired && ' (ID Required)'}
                                </span>
                              </div>
                            )}
                            {category.checkout && !category.isFree && (
                              <div className="info-row">
                                <strong>Payment:</strong> {category.checkout.provider}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && selectedEvent && (
        <EventAnalytics 
          eventId={selectedEvent.id} 
          onClose={closeModals}
        />
      )}
    </div>
  );
};

export default EventManager;
